import { useMemo } from 'react';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import HorizontalRuleIcon from '@mui/icons-material/HorizontalRule';
import LinkIcon from '@mui/icons-material/Link';
import { Box, LinearProgress, TextField, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { Link } from 'react-router-dom';
import { MetricCard, PageHeader, SectionCard, LoadingState, EmptyState } from '@/components/shared';
import { SQLTargets } from '@/services/ceoxSettings';
import { PipedriveDeal, PipedriveDealStage, PipedriveLead } from '@/services/pipedrive';

export type DatePreset =
  | 'this_month'
  | 'last_month'
  | 'this_quarter'
  | 'this_year'
  | 'last_year'
  | 'custom';
export type DateFilter = { preset: DatePreset; startDate: string; endDate: string };

export type PipedriveChannelRow = {
  channel: string;
  channelId: number | null;
  totalLeads: number;
  totalDeals: number;
  sqls: number;
  sqlTarget: number;
  won: number;
  wonValue: number;
  lost: number;
  lostValue: number;
  open: number;
  spend: number;
  cpl: number;
};

type Props = {
  isPipedriveConnected: boolean;
  deals: PipedriveDeal[];
  filteredDeals: PipedriveDeal[];
  filteredLeads: PipedriveLead[];
  stages: PipedriveDealStage[];
  isLoading: boolean;
  channelRows: PipedriveChannelRow[];
  sqlTargets: SQLTargets | null;
  dateFilter: DateFilter;
  onPresetChange: (preset: DatePreset) => void;
  onCustomDateChange: (startDate: string, endDate: string) => void;
};

const fmt = (amount: number): string =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(amount);

type ScorecardRow = {
  label: string;
  value: string;
  target?: string;
  status: 'green' | 'yellow' | 'red' | 'neutral';
  trend: 'up' | 'down' | 'flat';
};

const STATUS_COLORS = {
  green: { bg: '#F0FDF4', color: '#16A34A', border: '#BBF7D0' },
  yellow: { bg: '#FFFBEB', color: '#D97706', border: '#FDE68A' },
  red: { bg: '#FEF2F2', color: '#DC2626', border: '#FECACA' },
  neutral: { bg: '#F4F4F5', color: '#71717A', border: '#E4E4E7' },
};

function thresholdStatus(
  value: number,
  greenMin: number,
  yellowMin: number
): 'green' | 'yellow' | 'red' {
  if (value >= greenMin) return 'green';
  if (value >= yellowMin) return 'yellow';
  return 'red';
}

function thresholdStatusInverse(
  value: number,
  greenMax: number,
  yellowMax: number
): 'green' | 'yellow' | 'red' {
  if (value <= greenMax) return 'green';
  if (value <= yellowMax) return 'yellow';
  return 'red';
}

type ChannelStatus = 'on_track' | 'needs_attention' | 'off_track';

const CHANNEL_STATUS_CONFIG: Record<
  ChannelStatus,
  { label: string; bg: string; color: string; border: string }
> = {
  on_track: { label: 'On Track', bg: '#F0FDF4', color: '#16A34A', border: '#BBF7D0' },
  needs_attention: { label: 'Needs Attention', bg: '#FFFBEB', color: '#D97706', border: '#FDE68A' },
  off_track: { label: 'Off Track', bg: '#FEF2F2', color: '#DC2626', border: '#FECACA' },
};

const CHANNEL_COLORS: Record<string, string> = {
  'Cold Email': '#6366F1',
  'Cold Call': '#8B5CF6',
  Referral: '#10B981',
  Inbound: '#054AD9',
  'Google Ads': '#4285F4',
  ActiveCampaign: '#F59E0B',
  'Fintech Engineer': '#7C3AED',
  Uncategorized: '#71717A',
};

// Color the SQL number based on % of monthly target
function sqlColor(sqls: number, target: number): string {
  if (target <= 0) return '#09090B';
  const pct = sqls / target;
  if (pct >= 0.75) return '#16A34A'; // green — on pace
  if (pct >= 0.4) return '#D97706'; // yellow — behind
  return '#DC2626'; // red — way behind
}

const fmtNum = (n: number): string => new Intl.NumberFormat('en-US').format(n);

const CEOxDashboardComponent = function ({
  isPipedriveConnected,
  deals,
  filteredDeals,
  filteredLeads,
  stages,
  isLoading,
  channelRows,
  sqlTargets,
  dateFilter,
  onPresetChange,
  onCustomDateChange,
}: Props): JSX.Element {
  // --- Pipeline metrics (all-time for open pipeline, filtered for period) ---
  const pipelineMetrics = useMemo(() => {
    // All-time open pipeline
    const openDeals = deals.filter((d) => d.status === 'open');
    const pipelineValue = openDeals.reduce((s, d) => s + d.value, 0);

    // Period-specific (filtered by date range)
    const periodWon = filteredDeals.filter((d) => d.status === 'won');
    const periodLost = filteredDeals.filter((d) => d.status === 'lost');
    const periodNew = filteredDeals.length;
    const periodWonValue = periodWon.reduce((s, d) => s + d.value, 0);

    const totalClosed = periodWon.length + periodLost.length;
    const winRate = totalClosed > 0 ? Math.round((periodWon.length / totalClosed) * 100) : 0;

    // Avg deal cycle (won deals in period only)
    const cycles = periodWon
      .filter((d) => d.addTime && d.wonTime)
      .map((d) => {
        const add = new Date(d.addTime).getTime();
        const won = new Date(d.wonTime as string).getTime();
        return Math.round((won - add) / (1000 * 60 * 60 * 24));
      });
    const avgCycle =
      cycles.length > 0 ? Math.round(cycles.reduce((s, c) => s + c, 0) / cycles.length) : 0;

    return {
      openDeals: openDeals.length,
      pipelineValue,
      wonPeriod: periodWon.length,
      wonPeriodValue: periodWonValue,
      lostPeriod: periodLost.length,
      newPeriod: periodNew,
      winRate,
      avgCycle,
    };
  }, [deals, filteredDeals]);

  // --- Total SQLs from Pipedrive channel rows ---
  const totalSQLs = useMemo(() => channelRows.reduce((s, c) => s + c.sqls, 0), [channelRows]);
  const totalWon = useMemo(() => channelRows.reduce((s, c) => s + c.won, 0), [channelRows]);
  const totalLost = useMemo(() => channelRows.reduce((s, c) => s + c.lost, 0), [channelRows]);
  const totalWonValue = useMemo(
    () => channelRows.reduce((s, c) => s + c.wonValue, 0),
    [channelRows]
  );
  const totalSpend = useMemo(() => channelRows.reduce((s, c) => s + c.spend, 0), [channelRows]);

  // --- Scorecard rows ---
  const scorecardRows: ScorecardRow[] = useMemo(() => {
    const p = pipelineMetrics;
    const rows: ScorecardRow[] = [];

    if (isPipedriveConnected) {
      rows.push(
        {
          label: 'Leads',
          value: String(filteredLeads.length),
          status: thresholdStatus(filteredLeads.length, 20, 10),
          trend: 'flat',
        },
        {
          label: 'Deals',
          value: String(p.newPeriod),
          status: thresholdStatus(p.newPeriod, 10, 5),
          trend: 'flat',
        },
        {
          label: 'SQLs',
          value: String(totalSQLs),
          target: sqlTargets
            ? `/ ${
                sqlTargets.seo + sqlTargets.google_ads + sqlTargets.cold_email + sqlTargets.pipeline
              } target`
            : undefined,
          status: thresholdStatus(totalSQLs, 10, 5),
          trend: 'flat',
        },
        {
          label: 'Pipeline Value',
          value: fmt(p.pipelineValue),
          status: p.pipelineValue > 0 ? 'green' : 'neutral',
          trend: 'flat',
        },
        {
          label: 'Deals Won',
          value: `${p.wonPeriod} (${fmt(p.wonPeriodValue)})`,
          status: thresholdStatus(p.wonPeriod, 3, 1),
          trend: 'flat',
        },
        {
          label: 'Win Rate',
          value: `${p.winRate}%`,
          status: thresholdStatus(p.winRate, 30, 15),
          trend: 'flat',
        },
        {
          label: 'Avg Deal Cycle',
          value: `${p.avgCycle} days`,
          status: thresholdStatusInverse(p.avgCycle, 30, 60),
          trend: 'flat',
        }
      );
    }

    return rows;
  }, [pipelineMetrics, isPipedriveConnected, totalSQLs, sqlTargets, filteredLeads.length]);

  // --- Pipeline funnel data ---
  const funnelData = useMemo(() => {
    if (!isPipedriveConnected || stages.length === 0) return [];
    const openDeals = deals.filter((d) => d.status === 'open');
    const sortedStages = [...stages].sort((a, b) => a.order_nr - b.order_nr);

    return sortedStages
      .map((stage) => {
        const stageDeals = openDeals.filter((d) => d.stageId === stage.id);
        return {
          name: stage.name,
          count: stageDeals.length,
          value: stageDeals.reduce((s, d) => s + d.value, 0),
        };
      })
      .filter((s) => s.count > 0);
  }, [deals, stages, isPipedriveConnected]);

  // Channel status from SQLs + won/lost
  function channelStatus(sqls: number, won: number, lost: number): ChannelStatus {
    if (sqls === 0) return 'off_track';
    if (won > 0 && won >= lost) return 'on_track';
    if (lost > won) return 'needs_attention';
    return 'on_track';
  }

  const TrendIcon = ({ trend }: { trend: 'up' | 'down' | 'flat' }): JSX.Element => {
    if (trend === 'up') return <ArrowUpwardIcon sx={{ fontSize: 14, color: '#16A34A' }} />;
    if (trend === 'down') return <ArrowDownwardIcon sx={{ fontSize: 14, color: '#DC2626' }} />;
    return <HorizontalRuleIcon sx={{ fontSize: 14, color: '#71717A' }} />;
  };

  return (
    <>
      <PageHeader
        title="CEOx Dashboard"
        actions={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <ToggleButtonGroup
              value={dateFilter.preset}
              exclusive
              onChange={(_, val) => val && onPresetChange(val)}
              size="small"
              sx={{
                '& .MuiToggleButton-root': {
                  textTransform: 'none',
                  fontSize: 13,
                  px: 1.5,
                  py: 0.5,
                  borderColor: '#E4E4E7',
                  '&.Mui-selected': {
                    backgroundColor: '#054AD915',
                    color: '#054AD9',
                    borderColor: '#054AD9',
                    '&:hover': { backgroundColor: '#054AD925' },
                  },
                },
              }}
            >
              <ToggleButton value="this_month">This Month</ToggleButton>
              <ToggleButton value="last_month">Last Month</ToggleButton>
              <ToggleButton value="this_quarter">This Quarter</ToggleButton>
              <ToggleButton value="this_year">YTD</ToggleButton>
              <ToggleButton value="last_year">Last Year</ToggleButton>
              <ToggleButton value="custom">Custom</ToggleButton>
            </ToggleButtonGroup>
            {dateFilter.preset === 'custom' && (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  type="date"
                  size="small"
                  value={dateFilter.startDate}
                  onChange={(e) => onCustomDateChange(e.target.value, dateFilter.endDate)}
                  sx={{ '& .MuiInputBase-input': { fontSize: 13, py: 0.75 } }}
                />
                <TextField
                  type="date"
                  size="small"
                  value={dateFilter.endDate}
                  onChange={(e) => onCustomDateChange(dateFilter.startDate, e.target.value)}
                  sx={{ '& .MuiInputBase-input': { fontSize: 13, py: 0.75 } }}
                />
              </Box>
            )}
          </Box>
        }
      />

      {isLoading ? (
        <LoadingState message="Loading dashboard..." />
      ) : (
        <>
          {/* Connect Pipedrive prompt */}
          {!isPipedriveConnected && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                mb: 2,
                p: 2,
                borderRadius: '8px',
                backgroundColor: '#FEF3C7',
                border: '1px solid #FDE68A',
              }}
            >
              <LinkIcon sx={{ fontSize: 18, color: '#D97706' }} />
              <Box sx={{ fontSize: 13, color: '#92400E', flex: 1 }}>
                Connect your <strong>Pipedrive</strong> account to see pipeline and lead metrics.
              </Box>
              <Link
                to="/settings/integrations"
                style={{ fontSize: 13, color: '#054AD9', textDecoration: 'none', fontWeight: 500 }}
              >
                Connect now
              </Link>
            </Box>
          )}

          {/* KPI Cards */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(5, 1fr)' },
              gap: 2,
              mb: 3,
            }}
          >
            {isPipedriveConnected && (
              <>
                <MetricCard
                  label="Leads"
                  value={String(filteredLeads.length)}
                  subtitle={`${channelRows.length} channels`}
                  color="#6366F1"
                />
                <MetricCard
                  label="SQLs"
                  value={String(totalSQLs)}
                  subtitle="Qualified leads"
                  color="#054AD9"
                />
                <MetricCard
                  label="Pipeline Value"
                  value={fmt(pipelineMetrics.pipelineValue)}
                  subtitle={`${pipelineMetrics.openDeals} open deals`}
                  color="#7C3AED"
                />
                <MetricCard
                  label="Deals Won"
                  value={fmt(pipelineMetrics.wonPeriodValue)}
                  subtitle={`${pipelineMetrics.wonPeriod} deals`}
                  color="#10B981"
                />
                <MetricCard
                  label="Win Rate"
                  value={`${pipelineMetrics.winRate}%`}
                  subtitle={`${pipelineMetrics.wonPeriod}W / ${pipelineMetrics.lostPeriod}L`}
                  color="#F59E0B"
                />
              </>
            )}
          </Box>

          {/* Lead Generation by Channel (Pipedrive = source of truth) */}
          <SectionCard
            title="Lead Generation by Channel"
            subtitle={`${fmtNum(filteredLeads.length)} leads \u00B7 ${fmtNum(
              filteredDeals.length
            )} deals \u00B7 ${fmtNum(totalSQLs)} SQLs`}
          >
            {channelRows.length === 0 ? (
              <EmptyState
                title="No deals in this period"
                description={
                  isPipedriveConnected
                    ? 'No deals match the selected date range. Try a wider range.'
                    : 'Connect Pipedrive to see lead data by channel.'
                }
              />
            ) : (
              <Box
                sx={{
                  overflowX: 'auto',
                  WebkitOverflowScrolling: 'touch',
                  '& table': {
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: 13,
                    minWidth: 900,
                  },
                  '& th': {
                    textAlign: 'left',
                    padding: '8px 12px',
                    color: '#71717A',
                    fontWeight: 500,
                    fontSize: 11,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    borderBottom: '2px solid #E4E4E7',
                    whiteSpace: 'nowrap',
                  },
                  '& td': {
                    padding: '10px 12px',
                    borderBottom: '1px solid #F4F4F5',
                    whiteSpace: 'nowrap',
                  },
                  '& tbody tr': {
                    transition: 'background-color 200ms cubic-bezier(0.2, 0.8, 0.4, 1)',
                    '&:hover': { backgroundColor: '#FAFAFA' },
                    '&:active': { backgroundColor: '#F4F4F5' },
                  },
                }}
              >
                <table>
                  <thead>
                    <tr>
                      <th>Channel</th>
                      <th style={{ textAlign: 'right' }}>Leads</th>
                      <th style={{ textAlign: 'right' }}>Deals</th>
                      <th style={{ textAlign: 'right' }}>SQLs</th>
                      <th style={{ textAlign: 'right' }}>Spend</th>
                      <th style={{ textAlign: 'right' }}>CPL</th>
                      <th style={{ textAlign: 'center' }}>Won</th>
                      <th style={{ textAlign: 'center' }}>Lost</th>
                      <th style={{ textAlign: 'right' }}>Won Value</th>
                      <th style={{ textAlign: 'center' }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {channelRows.map((ch) => {
                      const st = CHANNEL_STATUS_CONFIG[channelStatus(ch.sqls, ch.won, ch.lost)];
                      const color = CHANNEL_COLORS[ch.channel] ?? '#71717A';
                      return (
                        <tr key={ch.channel}>
                          <td>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box
                                sx={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: '50%',
                                  backgroundColor: color,
                                  flexShrink: 0,
                                }}
                              />
                              <Box sx={{ fontWeight: 500, color: '#09090B' }}>{ch.channel}</Box>
                            </Box>
                          </td>
                          <td style={{ textAlign: 'right', fontWeight: 500, color: '#09090B' }}>
                            {fmtNum(ch.totalLeads)}
                          </td>
                          <td style={{ textAlign: 'right', color: '#71717A' }}>
                            {fmtNum(ch.totalDeals)}
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            <Box
                              component="span"
                              sx={{ fontWeight: 700, color: sqlColor(ch.sqls, ch.sqlTarget) }}
                            >
                              {fmtNum(ch.sqls)}
                            </Box>
                            <Box sx={{ fontSize: 10, color: '#71717A', lineHeight: 1.2 }}>
                              / {ch.sqlTarget} target
                            </Box>
                          </td>
                          <td style={{ textAlign: 'right', fontFamily: 'monospace' }}>
                            {ch.spend > 0 ? fmt(ch.spend) : '—'}
                          </td>
                          <td
                            style={{
                              textAlign: 'right',
                              fontFamily: 'monospace',
                              fontWeight: 500,
                              // eslint-disable-next-line no-nested-ternary
                              color: ch.cpl > 100 ? '#EF4444' : ch.cpl > 0 ? '#09090B' : '#71717A',
                            }}
                          >
                            {ch.cpl > 0 ? fmt(ch.cpl) : '—'}
                          </td>
                          <td style={{ textAlign: 'center', fontWeight: 700, color: '#16A34A' }}>
                            {ch.won > 0 ? ch.won : '—'}
                          </td>
                          <td style={{ textAlign: 'center', fontWeight: 700, color: '#DC2626' }}>
                            {ch.lost > 0 ? ch.lost : '—'}
                          </td>
                          <td
                            style={{ textAlign: 'right', fontFamily: 'monospace', fontWeight: 500 }}
                          >
                            {ch.wonValue > 0 ? fmt(ch.wonValue) : '—'}
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <Box
                              component="span"
                              sx={{
                                display: 'inline-block',
                                padding: '2px 8px',
                                borderRadius: '4px',
                                fontSize: 11,
                                fontWeight: 500,
                                backgroundColor: st.bg,
                                color: st.color,
                                border: `1px solid ${st.border}`,
                              }}
                            >
                              {st.label}
                            </Box>
                          </td>
                        </tr>
                      );
                    })}
                    {channelRows.length > 1 && (
                      <tr>
                        <td style={{ fontWeight: 700, color: '#09090B' }}>Total</td>
                        <td style={{ textAlign: 'right', fontWeight: 700, color: '#09090B' }}>
                          {fmtNum(filteredLeads.length)}
                        </td>
                        <td style={{ textAlign: 'right', fontWeight: 700, color: '#09090B' }}>
                          {fmtNum(filteredDeals.length)}
                        </td>
                        <td style={{ textAlign: 'right', fontWeight: 700, color: '#09090B' }}>
                          {fmtNum(totalSQLs)}
                        </td>
                        <td
                          style={{ textAlign: 'right', fontFamily: 'monospace', fontWeight: 500 }}
                        >
                          {totalSpend > 0 ? fmt(totalSpend) : '—'}
                        </td>
                        <td
                          style={{ textAlign: 'right', fontFamily: 'monospace', fontWeight: 500 }}
                        >
                          {totalSQLs > 0 && totalSpend > 0 ? fmt(totalSpend / totalSQLs) : '—'}
                        </td>
                        <td style={{ textAlign: 'center', fontWeight: 700, color: '#16A34A' }}>
                          {totalWon > 0 ? totalWon : '—'}
                        </td>
                        <td style={{ textAlign: 'center', fontWeight: 700, color: '#DC2626' }}>
                          {totalLost > 0 ? totalLost : '—'}
                        </td>
                        <td
                          style={{ textAlign: 'right', fontFamily: 'monospace', fontWeight: 700 }}
                        >
                          {totalWonValue > 0 ? fmt(totalWonValue) : '—'}
                        </td>
                        <td />
                      </tr>
                    )}
                  </tbody>
                </table>
              </Box>
            )}
          </SectionCard>

          <Box sx={{ mb: 3 }} />

          {/* Scorecard + Pipeline funnel */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', lg: isPipedriveConnected ? '1fr 1fr' : '1fr' },
              gap: 2,
              mb: 3,
            }}
          >
            {/* Scorecard */}
            <SectionCard title="Scorecard" subtitle="Key metrics at a glance">
              <Box
                sx={{
                  '& table': { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
                  '& th': {
                    textAlign: 'left',
                    padding: '8px 12px',
                    color: '#71717A',
                    fontWeight: 500,
                    fontSize: 11,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    borderBottom: '2px solid #E4E4E7',
                  },
                  '& td': {
                    padding: '10px 12px',
                    borderBottom: '1px solid #F4F4F5',
                    fontSize: 13,
                  },
                  '& tbody tr': {
                    transition: 'background-color 200ms cubic-bezier(0.2, 0.8, 0.4, 1)',
                    '&:hover': { backgroundColor: '#FAFAFA' },
                  },
                }}
              >
                <table>
                  <thead>
                    <tr>
                      <th>KPI</th>
                      <th>Value</th>
                      <th style={{ textAlign: 'center' }}>Status</th>
                      <th style={{ textAlign: 'center' }}>Trend</th>
                      <th>Target</th>
                    </tr>
                  </thead>
                  <tbody>
                    {scorecardRows.map((row) => {
                      const sc = STATUS_COLORS[row.status];
                      return (
                        <tr key={row.label}>
                          <td style={{ fontWeight: 500, color: '#09090B' }}>{row.label}</td>
                          <td style={{ fontWeight: 700, color: '#09090B' }}>{row.value}</td>
                          <td style={{ textAlign: 'center' }}>
                            <Box
                              component="span"
                              sx={{
                                display: 'inline-block',
                                width: 10,
                                height: 10,
                                borderRadius: '50%',
                                backgroundColor: sc.color,
                              }}
                            />
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <TrendIcon trend={row.trend} />
                          </td>
                          <td style={{ color: '#71717A', fontSize: 12 }}>{row.target ?? '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </Box>
            </SectionCard>

            {/* Pipeline funnel */}
            {isPipedriveConnected && (
              <SectionCard title="Pipeline Funnel" subtitle="Open deals by stage">
                {funnelData.length === 0 ? (
                  <EmptyState title="No open deals" description="Your pipeline is empty." />
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {(() => {
                      const maxCount = Math.max(...funnelData.map((s) => s.count), 1);
                      return funnelData.map((stage) => (
                        <Box key={stage.name}>
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              mb: 0.5,
                              fontSize: 13,
                            }}
                          >
                            <Box sx={{ fontWeight: 500, color: '#09090B' }}>{stage.name}</Box>
                            <Box sx={{ color: '#71717A' }}>
                              {stage.count} deal{stage.count !== 1 ? 's' : ''} &middot;{' '}
                              {fmt(stage.value)}
                            </Box>
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={(stage.count / maxCount) * 100}
                            sx={{
                              height: 8,
                              borderRadius: 4,
                              backgroundColor: '#F4F4F5',
                              '& .MuiLinearProgress-bar': {
                                backgroundColor: '#7C3AED',
                                borderRadius: 4,
                              },
                            }}
                          />
                        </Box>
                      ));
                    })()}
                  </Box>
                )}
              </SectionCard>
            )}
          </Box>
        </>
      )}
    </>
  );
};

export default CEOxDashboardComponent;
